import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, Calendar, Play } from 'lucide-react';
import { Button } from './ui/button';

const EpisodeCountdown = () => {
  const nextEpisodeDate = new Date('2026-04-21T01:00:00Z'); // Monday, April 20, 2026 at 9:00 PM EDT (New York)
  const episodeTitle = "Pregnant, No English, New Country: How Jamaica Became Home for 18 Years | Best of";
  const guestName = "Ysis Valdès";
  const episodeThumbnail = "https://img.youtube.com/vi/BApLilxUrfc/maxresdefault.jpg";

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isLive: false
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const distance = nextEpisodeDate.getTime() - now;

      if (distance < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isLive: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
        isLive: false
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, []);

  const TimeBox = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg mb-2 pulse-glow">
        <span className="text-2xl sm:text-3xl font-bold text-white">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
    </div>
  );

  if (timeLeft.isLive) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <Card className="p-6 bg-gradient-to-r from-primary to-secondary shadow-warm-lg">
            <div className="flex items-center justify-center gap-4 text-white">
              <Play className="w-8 h-8 animate-pulse" fill="white" />
              <div>
                <p className="text-2xl font-bold">New Episode Available Now!</p>
                <p className="text-white/90">{episodeTitle}</p>
              </div>
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full" onClick={() => window.open('https://youtu.be/BApLilxUrfc', '_blank')}>
                Watch Now
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <Card className="overflow-hidden shadow-warm-lg border-2 border-primary/20">
          <div className="grid md:grid-cols-[300px_1fr] gap-0">
            <div className="relative aspect-video md:aspect-auto overflow-hidden">
              <img src={episodeThumbnail} alt={episodeTitle} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/80 md:to-background" />
              <Badge className="absolute top-4 left-4 pulse-glow">Next Episode</Badge>
            </div>

            <div className="p-6 sm:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Monday, April 20 at 9:00 PM EDT (New York)</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-normal mb-2">{episodeTitle}</h3>
              <p className="text-muted-foreground mb-6">Featuring <span className="font-semibold text-foreground">{guestName}</span></p>

              <div className="flex items-center gap-3 sm:gap-6 mb-6">
                <TimeBox value={timeLeft.days} label="Days" />
                <span className="text-2xl text-muted-foreground">:</span>
                <TimeBox value={timeLeft.hours} label="Hours" />
                <span className="text-2xl text-muted-foreground">:</span>
                <TimeBox value={timeLeft.minutes} label="Min" />
                <span className="text-2xl text-muted-foreground hidden sm:inline">:</span>
                <TimeBox value={timeLeft.seconds} label="Sec" />
              </div>

              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open('https://www.youtube.com/@ALifeElsewhere?sub_confirmation=1', '_blank')}>
                  <Clock className="w-4 h-4 mr-2" />
                  Set Reminder
                </Button>
                <p className="text-xs text-muted-foreground">Subscribe to never miss an episode</p>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default EpisodeCountdown;